import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import { loadAdminStore, loadStore, deleteStore } from "./localStorage";
import { buildSignInUrl } from "./helpers/authReturn";

// eslint-disable-next-line no-console
console.log(process.env.NEXT_PUBLIC_API_BASE_URL);
export const baseURL = process.env.NEXT_PUBLIC_API_BASE_URL as string;
// Normalized once (no trailing slash) so URL joins never produce `//`.
const rawBackupURL = process.env
  .NEXT_PUBLIC_API_BACKUP_URL as string | undefined;
export const backupBaseURL = rawBackupURL?.replace(/\/+$/, "") || undefined;

/**
 * Statuses that mean the primary server (or its gateway) is down:
 * 502 Bad Gateway, 503 Service Unavailable, 504 Gateway Timeout.
 */
const FAILOVER_STATUSES = new Set([502, 503, 504]);

type FailoverConfig = AxiosRequestConfig & { _failoverRetried?: boolean };

const isAbsoluteUrl = (url?: string) => /^https?:\/\//i.test(url ?? "");

// --- Circuit breaker (sticky failover) ------------------------------------
// Once the primary is observed down, route ALL subsequent requests straight
// to the backup for RECOVERY_CHECK_INTERVAL_MS instead of paying
// (primary timeout + backup latency) on every single call. After the
// cooldown the breaker resets to half-open: the next request probes the
// primary, and a fresh failure trips the breaker again.
let isPrimaryDown = false;
let recoveryTimer: ReturnType<typeof setTimeout> | null = null;
const RECOVERY_CHECK_INTERVAL_MS = 60_000;

/** True while traffic is pinned to the backup origin. */
export function isBackupActive() {
  return isPrimaryDown;
}

function markPrimaryDown() {
  if (isPrimaryDown || !backupBaseURL) return;
  isPrimaryDown = true;
  if (recoveryTimer) clearTimeout(recoveryTimer);
  recoveryTimer = setTimeout(() => {
    isPrimaryDown = false;
    recoveryTimer = null;
  }, RECOVERY_CHECK_INTERVAL_MS);
}

/**
 * Swap the origin of an absolute URL onto the backup, preserving path,
 * query and hash. Falls back to slash-safe string surgery if parsing fails.
 */
function swapOrigin(originalUrl: string, newBase: string): string {
  try {
    const urlObj = new URL(originalUrl);
    const newBaseObj = new URL(newBase);
    urlObj.protocol = newBaseObj.protocol;
    urlObj.host = newBaseObj.host; // hostname + port
    return urlObj.toString();
  } catch {
    const path = originalUrl.replace(/^https?:\/\/[^/]+/, "");
    return newBase + (path.startsWith("/") ? path : `/${path}`);
  }
}

function targetsPrimary(
  config: FailoverConfig,
  instance?: AxiosInstance,
): boolean {
  // Absolute URL pointing at the primary host, or a relative URL (which
  // resolves against the instance's primary baseURL).
  if (typeof config.url === "string" && isAbsoluteUrl(config.url)) {
    return !!baseURL && config.url.startsWith(baseURL);
  }
  const effectiveBase = config.baseURL ?? instance?.defaults.baseURL;
  return !effectiveBase || effectiveBase === baseURL;
}

/** Point a primary-targeted request config at the backup origin. */
function pointAtBackup(config: FailoverConfig) {
  if (
    typeof config.url === "string" &&
    isAbsoluteUrl(config.url) &&
    baseURL &&
    config.url.startsWith(baseURL) &&
    backupBaseURL
  ) {
    config.url = swapOrigin(config.url, backupBaseURL);
    config.baseURL = undefined;
  } else {
    config.baseURL = backupBaseURL;
  }
}

function shouldFailover(
  error: AxiosError,
  instance?: AxiosInstance,
): boolean {
  if (!backupBaseURL) return false;
  const config = error.config as FailoverConfig | undefined;
  if (!config || config._failoverRetried) return false;
  if (!targetsPrimary(config, instance)) return false;

  // Gateway/down statuses from the primary.
  if (error.response && FAILOVER_STATUSES.has(error.response.status)) {
    return true;
  }

  // No response at all (connection refused / DNS failure / timeout) while
  // the client is online means the primary is unreachable — try the backup.
  // Skip when the browser reports offline so we don't double-fail every call.
  if (!error.response && error.request) {
    if (typeof navigator === "undefined") return true;
    return navigator.onLine !== false;
  }

  return false;
}

function retryWithBackup(instance: AxiosInstance, error: AxiosError) {
  const config = error.config as FailoverConfig;
  config._failoverRetried = true;
  markPrimaryDown();
  pointAtBackup(config);
  return instance(config);
}

/**
 * Sticky routing: while the breaker is tripped, send primary-targeted
 * requests straight to the backup so each call pays backup latency only
 * (no failed primary attempt first).
 */
function attachFailoverRequestInterceptor(instance: AxiosInstance) {
  instance.interceptors.request.use((config) => {
    if (!isPrimaryDown || !backupBaseURL) return config;
    const failoverConfig = config as FailoverConfig;
    if (!targetsPrimary(failoverConfig, instance)) return config;
    pointAtBackup(failoverConfig);
    return config;
  });
}

/**
 * Retries a request once against NEXT_PUBLIC_API_BACKUP_URL when the primary
 * is down (502/503/504, or unreachable) and trips the circuit breaker so
 * later requests go straight to the backup. No-op when the backup URL isn't
 * configured. The _failoverRetried flag guarantees at most one backup
 * attempt per request.
 *
 * IMPORTANT — registration order: this must be attached BEFORE
 * attachAuthInterceptors. Axios runs response interceptors in registration
 * order, so failover sees errors first. Otherwise a 401 from a
 * misbehaving/dead gateway would trigger a token refresh against the dead
 * primary (and log the user out) before failover ever got a chance.
 */
function attachFailoverInterceptor(instance: AxiosInstance) {
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      if (!shouldFailover(error, instance)) {
        return Promise.reject(error);
      }
      try {
        return await retryWithBackup(instance, error);
      } catch (backupError) {
        return Promise.reject(backupError);
      }
    },
  );
}

export const axiosWithoutToken = axios.create({
  baseURL,
  withCredentials: true,
  headers: {
    "Content-type": "application/json",
  },
});
attachFailoverRequestInterceptor(axiosWithoutToken);
attachFailoverInterceptor(axiosWithoutToken);

export const getStore = () => {
  const userStore: any = loadStore();
  const adminStore: any = loadAdminStore();

  // Find whichever store contains a truthy token parameter
  const activeAuthData = adminStore?.token ? adminStore : userStore;
  return activeAuthData;
};

/**
 * The httpOnly session (access_token / refresh_token cookies) is attached by
 * the browser automatically because withCredentials is enabled, so user
 * requests need no manual Authorization header. Admin auth (separate system,
 * Bearer token in sessionStorage) still relies on the header, so it is kept
 * as a fallback when a store token exists.
 */
export const axiosWithToken = (config?: AxiosRequestConfig): AxiosInstance => {
  const activeAuthData = getStore();

  const instance = axios.create({
    baseURL,
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
      ...(activeAuthData?.token
        ? { Authorization: `Bearer ${activeAuthData.token}` }
        : {}),
    },
    ...config,
  });

  // Failover first (see attachFailoverInterceptor docstring for why order
  // matters), then auth refresh handling.
  attachFailoverRequestInterceptor(instance);
  attachFailoverInterceptor(instance);
  attachAuthInterceptors(instance);
  return instance;
};

/**
 * Module-level shared refresh promise. Exactly one /auth/refresh_token call is
 * ever in flight app-wide; concurrent 401s all await the SAME promise.
 */
let refreshPromise: Promise<boolean> | null = null;

/** Registered by the AuthProvider so a terminal refresh failure (expired
 * session) routes through the same teardown used during boot. */
let onSessionExpiredHandler: (() => void) | null = null;

export function registerSessionExpiredHandler(handler: () => void) {
  onSessionExpiredHandler = handler;
}

export function runSessionExpiredHandler() {
  if (onSessionExpiredHandler) onSessionExpiredHandler();
}

const REFRESH_URL = "/auth/refresh_token";

async function refreshSession(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = axiosWithoutToken
      .get(REFRESH_URL)
      .then((r) => r.status === 200)
      .catch(() => false)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

function attachAuthInterceptors(instance: AxiosInstance) {
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const { response, config } = error;
      const status = response?.status;

      if (status !== 401 || !config) {
        return Promise.reject(error);
      }

      // Never retry the refresh call itself — rotation invalidates it.
      const isRefreshCall =
        typeof config.url === "string" &&
        config.url.includes("/auth/refresh_token");
      if (isRefreshCall) {
        return Promise.reject(error);
      }

      // Never retry a request more than once after refresh.
      const retried = (config as AxiosRequestConfig & { _retried?: boolean })
        ._retried;
      if (retried) {
        return Promise.reject(error);
      }
      (config as AxiosRequestConfig & { _retried?: boolean })._retried = true;

      const refreshed = await refreshSession();
      if (refreshed) {
        return instance(config);
      }

      // Refresh failed — session is expired. Route through the AuthProvider
      // teardown (logout, state reset, notify, redirect).
      // EXCEPTION: while failed over to the backup, a refresh failure is
      // ambiguous — the backup may live on a different origin/cookie domain
      // and reject the primary's httpOnly session cookie with a 401 even
      // though the session is fine. Destroying the session on that signal
      // would log users out during every primary outage, so just reject and
      // let the normal flow resume once the breaker resets to the primary.
      if (!isPrimaryDown) {
        runSessionExpiredHandler();
      }
      return Promise.reject(error);
    },
  );
}

export const handleAxiosError = (err: any, thunkAPI: any) => {
  const { message, status } = err.toJSON();

  if (status === 403) {
    deleteStore();
    // Never bounce visitors off public pages — they simply become anonymous.
    const pathname =
      typeof window !== "undefined" ? window.location.pathname : "";
    const isPublicRoute =
      !pathname.startsWith("/user") && !pathname.startsWith("/admin");
    if (!isPublicRoute) {
      window.location.assign(buildSignInUrl(pathname + window.location.search));
    }
  }

  if (message === "Network Error") {
    throw thunkAPI.rejectWithValue({
      status: 500,
      message: "You are offline",
    });
  } else {
    throw thunkAPI.rejectWithValue({
      status: err.response.status || 0,
      message: err.response.data.message,
    });
  }
};
